import { Band, BandStack } from '@/components/ui/Band'
import { ButtonLink } from '@/components/ui/Button'
import { PageHero } from '@/components/ui/PageHero'
import { Eyebrow, SourceRef } from '@/components/ui/Section'
import { Stagger } from '@/components/motion/Reveal'
import { useDocumentMeta } from '@/lib/seo'
import { ROUTES } from '@/lib/site'

const TOTAL_LAYERS = 5

const PANEL_FIELDS: [string, string][] = [
  ['Tier', 'which rung of the ladder answered'],
  ['Decoder', 'the named model that produced it'],
  ['Granularity', 'day, week, month or quarter — as recorded'],
  ['Points', 'native, trained on, held back'],
  ['Quantile source', 'native quantiles, or empirical paths'],
  ['Calibration', 'error on holdout, fit, and whether history was sufficient'],
]

const LADDER = [
  {
    id: 'T0',
    name: 'Fixture',
    detail:
      'The design reference path, used to build and review the interface. Never mixed with a customer snapshot, and labelled as fixture wherever it appears.',
  },
  {
    id: 'T1',
    name: 'TimesFM',
    detail:
      'A time-series foundation model with native quantiles. The weights are 925 MB and are not in the image, so on a fresh install this rung is unavailable until they are fetched deliberately.',
  },
  {
    id: 'T2',
    name: 'TimesFM with exogenous regressors',
    detail:
      'The same model given series the data plane established independently — never a regressor invented to improve the fit.',
  },
  {
    id: 'T3',
    name: 'Classical',
    detail:
      'Where the ladder starts when the weights are absent. Well-understood statistical models with intervals that can be checked by hand.',
  },
  {
    id: 'T4',
    name: 'Bootstrap',
    detail:
      'Empirical paths resampled from the recorded series, when a parametric fit is not defensible. The band comes from the paths themselves.',
  },
  {
    id: 'T5',
    name: 'Locked',
    detail:
      'Not enough recorded history to project at all. The panel states the lock reason and what it would need, and shows no band.',
  },
]

const CALIBRATION = [
  {
    name: 'Holdout periods',
    detail: 'How many were withheld, stated per panel. A fit reported without this is unreadable.',
  },
  {
    name: 'Error and fit',
    detail:
      'Mean absolute error and a fit percentage, both nullable — absent when there was not enough history to compute them honestly.',
  },
  {
    name: 'Sufficient',
    detail:
      'A plain boolean. When it is false the panel carries a low-confidence treatment rather than a number that looks like the others.',
  },
  {
    name: 'Target, when one exists',
    detail:
      'A target is a stated intention, drawn in its own colour, never blended into the projection.',
  },
]

export default function Projection() {
  useDocumentMeta(ROUTES.projection)

  return (
    <BandStack>
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="projection">
        <PageHero
          crumb="Projection"
          title="The next three quarters, as a range."
          lede="A projection is p10, p50 and p90 per period. Not because a point estimate is hard to compute, but because publishing one claims a precision the data does not carry — and anyone reading a single number will treat it as a commitment."
        />
        <p className="mx-auto mt-6 max-w-[62ch] text-pretty text-center text-[15px] leading-[1.75] text-muted">
          Each recommended action is re-simulated through the same machinery, so its effect is
          comparable with the baseline rather than asserted.
        </p>

        <div className="mx-auto mt-12 max-w-3xl card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-lift sm:p-8">
          <Eyebrow>What a projected panel carries</Eyebrow>
          <dl className="mt-5 grid gap-3">
            {PANEL_FIELDS.map(([term, detail]) => (
              <div
                key={term}
                className="grid gap-1 border-b border-line pb-3 last:border-b-0 last:pb-0 sm:grid-cols-[170px_1fr] sm:gap-4"
              >
                <dt className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-primary">
                  {term}
                </dt>
                <dd className="text-[13.5px] leading-[1.65] text-muted">{detail}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-5 border-t border-line pt-4 text-[13.5px] leading-[1.7] text-muted">
            If the last row says insufficient, the panel locks. It does not degrade quietly into a
            wider band.
          </p>
        </div>
      </Band>

      <Band layer={2} total={TOTAL_LAYERS} tone="warm" labelledBy="ladder-heading">
        <Eyebrow className="text-primary">The ladder</Eyebrow>
        <h2
          id="ladder-heading"
          className="mt-6 max-w-3xl text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[38px]"
        >
          Six tiers, tried in order. The panel names the one that answered.
        </h2>
        <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
          Descending the ladder is the normal case, not a failure state. What would be a failure is
          a lower tier presented as a higher one.
        </p>
        <Stagger variant="up" step={80} as="ol" itemAs="li" className="mt-10 grid gap-3">
          {LADDER.map((tier) => (
            <div
              key={tier.id}
              className="flex flex-col gap-3 card-spot rounded-card border border-line bg-white/85 backdrop-blur p-5 shadow-card sm:flex-row sm:gap-6 sm:p-6"
            >
              <span className="font-mono text-[12px] text-primary sm:w-10 sm:flex-none">
                {tier.id}
              </span>
              <span className="min-w-0">
                <strong className="block text-[16px] font-extrabold tracking-[-0.015em]">
                  {tier.name}
                </strong>
                <span className="mt-2 block max-w-measure text-[13.5px] leading-[1.7] text-muted">
                  {tier.detail}
                </span>
              </span>
            </div>
          ))}
        </Stagger>
      </Band>

      <Band layer={3} total={TOTAL_LAYERS} tone="ice" labelledBy="gate-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow className="text-primary">The gate</Eyebrow>
            <h2
              id="gate-heading"
              className="mt-6 text-balance text-[27px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
            >
              A projection is never labelled TimesFM unless it came from TimesFM.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              When the weights are missing, the vendored loader does not fail loudly enough to be
              safe: it returns a stub that answers in the same shape, and the payload still reports
              the same source string either way. The only reliable signal is the decoder name.
            </p>
            <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
              So the gate checks the decoder name, and a stub result falls through to classical
              models. The decoder is also persisted with every stored forecast, which means a number
              produced by a stub stays discoverable months later rather than becoming
              indistinguishable from a real one.
            </p>
            <div className="mt-5">
              <SourceRef>bi/forecast/ladder.py · bi/engine_bridge.py</SourceRef>
            </div>
          </div>

          <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card sm:p-8">
            <h3 className="text-[17px] font-extrabold tracking-[-0.015em]">
              Why a name check and not a health check
            </h3>
            <p className="mt-4 text-[14px] leading-[1.75] text-muted">
              There is no health endpoint on the vendored forecasting module. It exposes three
              functions and nothing that reports readiness, so trusting the call would mean trusting
              a stub.
            </p>
            <div className="mt-5 rounded-card border border-line bg-surface-2 p-5">
              <Eyebrow>Measured, not assumed</Eyebrow>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">
                On a clean monotonic ramp, the stub&rsquo;s first forecast point came back at a
                value the real model would not produce. That single measurement is why the gate
                exists in code rather than in a comment.
              </p>
            </div>
            <p className="mt-5 text-[13.5px] leading-[1.7] text-muted">
              The projection, temporal and ensemble flags all default off, so nothing can serve a
              projection by accident during development.
            </p>
          </div>
        </div>
      </Band>

      <Band layer={4} total={TOTAL_LAYERS} tone="mint" labelledBy="calibration-heading">
        <Eyebrow className="text-primary">Calibration</Eyebrow>
        <h2
          id="calibration-heading"
          className="mt-6 max-w-3xl text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[38px]"
        >
          Every projection is scored against history it was not shown.
        </h2>
        <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
          Periods are held back, the model projects them, and the error is reported on the panel
          next to the band. A backtest series is carried alongside the recorded one so the two can
          be drawn together and compared by eye.
        </p>
        <Stagger
          variant="up"
          step={90}
          as="ul"
          itemAs="li"
          className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {CALIBRATION.map((item) => (
            <div
              key={item.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
            >
              <h3 className="text-[15.5px] font-extrabold tracking-[-0.015em]">{item.name}</h3>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{item.detail}</p>
            </div>
          ))}
        </Stagger>
      </Band>

      <Band layer={5} total={TOTAL_LAYERS} tone="ice" labelledBy="projection-cta" showMark={false}>
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="projection-cta"
            className="text-balance text-[26px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
          >
            A projection is only as good as the extraction under it.
          </h2>
          <p className="mx-auto mt-5 max-w-[54ch] text-pretty text-[15px] leading-[1.75] text-muted">
            Which is why the trace goes back past the model, through the calculation, to the cell.
          </p>
          <div className="mt-9 flex justify-center">
            <ButtonLink to={ROUTES.provenance.path} size="lg">
              See the trace
            </ButtonLink>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
